import React, {
    useState,
    useContext,
    useCallback,
    useRef,
    useMemo,
    useEffect,
} from "react";
import moment from "moment";
import AuthContext from "../../contexts/auth-context.js";
import { useNavigate } from "react-router-dom";
import { getTeam } from "../../util.js";
import { getRetailAPI, withAllOverviewFeatures, USE_DUMMY_RETAIL_OVERVIEW } from "./dummyLiveData.js";
import { LinearProgress } from "@mui/material";
import Icon from "../../components/atoms/Icon.js";
import { Panel, PanelHeader, InfoTip, StatTile } from "../Concession/components/ConcessionUI";
import { ChartSegmented } from "../Concession/charts/chartTheme";
import TrafficShareDonut from "./overview/TrafficShareDonut";
import ConversionOccupancyChart from "./charts/ConversionOccupancyChart";
import { zoneExportRows, buildZoneCsv, zoneExportFilename, downloadCsv } from "./overview/zoneExports";
import { KpiCard } from "../Concession/live/LiveComponents";
import Presence from "../Concession/components/Presence";
import { TimeWindow } from "../Concession/ConcessionOverview";
import OverviewCompareChart from "./overview/OverviewCompareChart";
import {
    processOccupancy,
    buildGameMetrics,
    bucketSeries,
    occupancyPoints,
    minuteToClock,
} from "./overview/overviewMetrics";
import { PHASES, PURCHASE_COLOR, zoneHeatColor, dwellHeatColor } from "./retailPalette";
import "../Concession/concession.css";
import "./retail.css";

const MAX_GAMES = 4;

const overviewColors = [
    '#3e58c0', // Indigo
    '#e88559', // Tangerine
    '#9792ec', // Lilac
    '#aa4851', // Scarlet
    '#866263', // Royal Purple
    '#76ba92', // Key Lime
    '#0bcaca', // Light Turquoise
    '#f1dc78', // Lemon Yellow
    '#3c6e5f', // Army Green
];

const fmt = (n) => (typeof n === 'number' && !Number.isNaN(n) ? Math.round(n).toLocaleString() : '--');

const SeasonSelector = ({ games, currentSeason, setCurrentSeason }) => {
    const seasons = useMemo(() => {
        const years = new Set();
        games.forEach((game) => game.dates.forEach((d) => years.add(moment(d.date).format('YYYY'))));
        return Array.from(years).sort((a, b) => b - a);
    }, [games]);

    return (
        <div className="cx-season">
            <span className="cx-chart-segment-label">Season</span>
            <div className="cx-chart-segmented">
                {seasons.map((season) => (
                    <button
                        key={season}
                        type="button"
                        className={`cx-chart-segment ${currentSeason === season ? 'active' : ''}`}
                        onClick={() => setCurrentSeason(season)}
                    >
                        {season}
                    </button>
                ))}
            </div>
        </div>
    );
};

// Thin bar split into Pregame / Gates open / Game, with counts underneath
const PhaseBar = ({ phases }) => {
    const parts = [
        { key: 'pregame', ...PHASES.pregame, value: phases.pregame },
        { key: 'gates', ...PHASES.gates, value: phases.gates },
        { key: 'game', ...PHASES.game, value: phases.game },
    ];
    const total = parts.reduce((s, p) => s + (p.value || 0), 0);
    return (
        <div className="rx-ov-phase">
            <div className="rx-ov-phase-bar">
                {parts.map((p) => (
                    <span
                        key={p.key}
                        style={{ width: `${total ? (p.value / total) * 100 : 0}%`, background: p.color }}
                        title={`${p.label}: ${fmt(p.value)}`}
                    />
                ))}
            </div>
            <div className="rx-ov-phase-keys">
                {parts.map((p) => (
                    <span key={p.key}>
                        <i style={{ background: p.color }} />
                        {p.label} <strong>{fmt(p.value)}</strong>
                    </span>
                ))}
            </div>
        </div>
    );
};

// Conversion as a bar: share of day traffic associated with a purchase
const ConversionBar = ({ conversion, best }) => (
    <div className="rx-ov-conv">
        <div className="rx-ov-conv-head">
            <span>Conversion</span>
            <strong className={best ? 'best' : ''}>{conversion.rate}%</strong>
        </div>
        <div className="rx-ov-conv-bar">
            <span style={{ width: `${Math.min(100, conversion.rate)}%`, background: PURCHASE_COLOR }} />
        </div>
        <div className="rx-ov-conv-foot">{fmt(conversion.purchases)} of {fmt(conversion.base)} visitors associated with a purchase</div>
    </div>
);

export default function Overview({
    currentLocation: locationProp,
    games,
    findLabelByZoneId,
}) {
    // Same store config, unless the Overview dummy test mode adds the missing sensor types
    const currentLocation = useMemo(() => withAllOverviewFeatures(locationProp), [locationProp]);
    const navigate = useNavigate();
    const authCtx = useContext(AuthContext);
    // Same as util's getQueuesAPI unless dummy data mode is on (local dev only)
    const getQueuesAPI = getRetailAPI(currentLocation);

    // Occupancy needs zones and a capacity (same as Game Day's maxOccupancy > 0); door-only stores have neither
    const hasOccupancy = currentLocation.maxOccupancy > 0 && (currentLocation.zones || []).length > 0;
    const hasDoors = (currentLocation.doorCounterSensorIds || []).length > 0;
    const hasSales = (currentLocation.doorCounterSensorSalesIds || []).length > 0;
    const hasQueues = (currentLocation.queueTimeSensorIds || []).length > 0;

    const zoneName = useCallback((id) => {
        const label = findLabelByZoneId ? findLabelByZoneId(id) : null;
        return label || currentLocation.zones?.find((z) => z.id === id)?.label || `Zone ${id}`;
    }, [findLabelByZoneId, currentLocation.zones]);

    const [currentSeason, setCurrentSeason] = useState(() => {
        const years = new Set();
        games.forEach((game) => game.dates.forEach((d) => years.add(moment(d.date).format('YYYY'))));
        return Array.from(years).sort((a, b) => b - a)[0] || moment().format('YYYY');
    });

    // Selected games keep their color for as long as they're selected.
    // Data is stored by date, so a slow response can never land on the wrong game.
    const [selected, setSelected] = useState([]); // [{ date, color }]
    const [dataByDate, setDataByDate] = useState({}); // raw responses by date
    // Time window relative to each game's start (same default as concessions)
    const [timeRange, setTimeRange] = useState({ startOffset: -180, endOffset: 240 });
    const [pending, setPending] = useState([]);
    const loading = pending.length > 0;

    // "Add game" dropdown
    const [pickerOpen, setPickerOpen] = useState(false);
    const pickerRef = useRef(null);
    useEffect(() => {
        if (!pickerOpen) return undefined;
        const onDown = (e) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target)) setPickerOpen(false);
        };
        const onKey = (e) => e.key === 'Escape' && setPickerOpen(false);
        window.addEventListener('pointerdown', onDown);
        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('pointerdown', onDown);
            window.removeEventListener('keydown', onKey);
        };
    }, [pickerOpen]);
    useEffect(() => {
        if (selected.length >= MAX_GAMES) setPickerOpen(false);
    }, [selected.length]);

    const findGameByDate = useCallback((date) => {
        for (const series of games) {
            const index = series.dates.findIndex((d) => d.date === date);
            if (index !== -1) return { series, gameDate: series.dates[index], index };
        }
        return null;
    }, [games]);

    const gameLabel = useCallback((date) => {
        const found = findGameByDate(date);
        const opponent = found ? getTeam(currentLocation.league, found.series.teamID)?.name : null;
        return {
            short: moment(date).format('MMM D'),
            long: moment(date).format('ddd, MMM D'),
            sub: found ? `${opponent ? `vs ${opponent} · ` : ''}Game ${found.index + 1}` : '',
            opponent,
        };
    }, [findGameByDate, currentLocation.league]);

    const handleAuthError = (error) => {
        if (error?.status === 401) {
            authCtx.logout();
            navigate("/auth");
        } else {
            alert("Unable to fetch data. Please refresh the page and try again.");
        }
    };

    const fetchGame = (dateToFetch) => {
        const day = moment(dateToFetch).format("YYYY-MM-DD");
        setPending((p) => [...p, dateToFetch]);

        // Occupancy (stores that track it)
        const occupancyPromise = new Promise((resolve, reject) => {
            if (!hasOccupancy) {
                resolve(null);
                return;
            }
            getQueuesAPI(
                "get-retail-location-daily-data-v2",
                {
                    timezone: currentLocation.timeZone,
                    zones: currentLocation.zones,
                    venue: authCtx.venue,
                    locationId: currentLocation.id,
                    measurement: currentLocation.measurement,
                    date: day,
                },
                (data_, error) => {
                    if (error) {
                        handleAuthError(error);
                        reject(error);
                        return;
                    }
                    try {
                        resolve(processOccupancy(data_, currentLocation, dateToFetch));
                    } catch (e) {
                        console.error(`Error processing data for ${dateToFetch}:`, e);
                        reject(e);
                    }
                },
                authCtx.token
            );
        });

        // Door counts (sales and stairs sensors excluded)
        const trafficPromise = new Promise((resolve, reject) => {
            if (!hasDoors) {
                resolve(null);
                return;
            }
            getQueuesAPI(
                "get-location-daily-door-count-v2",
                {
                    timezone: currentLocation.timeZone,
                    venue: authCtx.venue,
                    locationId: currentLocation.id,
                    excludedSensorIds: [
                        ...(currentLocation.doorCounterSensorSalesIds || []),
                        ...(currentLocation.doorCounterSensorStairsIds || []),
                    ],
                    measurement: currentLocation.measurement,
                    doorMeasurement: currentLocation.doorMeasurement,
                    date: day,
                },
                (trafficData, error) => {
                    if (error) {
                        handleAuthError(error);
                        reject(error);
                        return;
                    }
                    resolve(trafficData);
                },
                authCtx.token
            );
        });

        // Sales counters (all sensors) for conversion
        const salesPromise = new Promise((resolve) => {
            if (!hasSales || day < "2024-10-01") {
                resolve(null);
                return;
            }
            getQueuesAPI(
                "get-location-daily-door-count-v2",
                {
                    timezone: currentLocation.timeZone,
                    venue: authCtx.venue,
                    locationId: currentLocation.id,
                    excludedSensorIds: [],
                    measurement: currentLocation.measurement,
                    doorMeasurement: currentLocation.doorMeasurement,
                    date: day,
                },
                (salesData, error) => {
                    if (error || !salesData) {
                        console.warn("Failed to fetch sales counter data for", dateToFetch);
                        resolve(null);
                    } else {
                        resolve(salesData);
                    }
                },
                authCtx.token
            );
        });

        // Queue times (stores with queue sensors): each reading averaged across queue locations
        const queuePromise = new Promise((resolve) => {
            if (!hasQueues) {
                resolve(null);
                return;
            }
            getQueuesAPI(
                "get-retail-location-daily-queue-time",
                {
                    timezone: currentLocation.timeZone,
                    venue: authCtx.venue,
                    locationId: currentLocation.id,
                    measurement: currentLocation.measurement,
                    queueMeasurement: currentLocation.queueMeasurement,
                    queueTimeSensorIds: currentLocation.queueTimeSensorIds,
                    date: day,
                },
                (data, error) => {
                    if (!data) {
                        console.error("Failed to fetch daily queue time");
                        resolve(null);
                        return;
                    }
                    resolve(data.map((entry) => {
                        const values = entry.queueTime && typeof entry.queueTime === 'object'
                            ? Object.values(entry.queueTime).filter((v) => typeof v === 'number')
                            : [entry.queueTime || 0];
                        const avg = values.length ? values.reduce((s, v) => s + v, 0) / values.length : 0;
                        return { timestamp: entry.timestamp, queueTime: Math.round(avg * 10) / 10 };
                    }));
                },
                authCtx.token
            );
        });

        Promise.all([occupancyPromise, trafficPromise, salesPromise, queuePromise])
            .then(([occupancy, traffic, sales, queue]) => {
                setDataByDate((prev) => ({ ...prev, [dateToFetch]: { occupancy, traffic, sales, queue } }));
            })
            .catch((error) => {
                console.error("Error fetching overview data:", error);
            })
            .finally(() => {
                setPending((p) => p.filter((d) => d !== dateToFetch));
            });
    };

    const toggleGame = (date) => {
        const isSelected = selected.some((s) => s.date === date);
        if (isSelected) {
            setSelected((prev) => prev.filter((s) => s.date !== date));
            return;
        }
        if (selected.length >= MAX_GAMES) {
            alert("Maximum amount of games is 4.");
            return;
        }
        const used = selected.map((s) => s.color);
        const color = overviewColors.find((c) => !used.includes(c)) || overviewColors[0];
        setSelected((prev) => [...prev, { date, color }]);
        if (!dataByDate[date]) fetchGame(date);
    };

    const clearGames = () => setSelected([]);

    // Overview dummy test mode: pick the 4 most recent finished games once the schedule loads
    const autoPicked = useRef(false);
    useEffect(() => {
        if (!USE_DUMMY_RETAIL_OVERVIEW || autoPicked.current || !games.length) return;
        const today = moment().format('YYYY-MM-DD');
        const dates = games
            .flatMap((series) => series.dates.map((d) => d.date))
            .filter((d) => d < today)
            .sort()
            .reverse()
            .slice(0, MAX_GAMES);
        if (!dates.length) return;
        autoPicked.current = true;
        setSelected(dates.map((date, i) => ({ date, color: overviewColors[i] })));
        dates.forEach((date) => fetchGame(date));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [games]);

    const filteredGames = games
        .filter((x) => new Date() > moment(x.dates[0].date))
        .filter((x) => moment(x.dates[0].date).format("YYYY") === currentSeason);

    // Numbers for each loaded game, recalculated when the time window changes
    const metricsByDate = useMemo(() => {
        const out = {};
        Object.entries(dataByDate).forEach(([date, raw]) => {
            out[date] = buildGameMetrics({
                date,
                gameDate: findGameByDate(date)?.gameDate,
                ...raw,
                currentLocation,
                window: timeRange,
            });
        });
        return out;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataByDate, timeRange, games]);

    // Selected games with their numbers, in the order they were picked
    const cards = selected.map((s) => ({
        ...s,
        label: gameLabel(s.date),
        pending: pending.includes(s.date),
        m: metricsByDate[s.date] || null,
    }));
    const ready = cards.filter((c) => c.m);
    const hasSelection = selected.length > 0;

    // Headline numbers, averaged per game
    const avgOf = (values) => {
        const v = values.filter((x) => typeof x === 'number');
        return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
    };
    const kpi = useMemo(() => {
        const withTraffic = ready.filter((c) => c.m.traffic);
        const withConv = ready.filter((c) => c.m.conversion);
        const withOcc = ready.filter((c) => c.m.occupancy);
        const withQueue = ready.filter((c) => c.m.queue && c.m.queue.avg != null);
        const purchases = withConv.reduce((s, c) => s + c.m.conversion.purchases, 0);
        const base = withConv.reduce((s, c) => s + c.m.conversion.base, 0);
        // Highest single occupancy moment across the games (which game, when, % full)
        const peakOccMax = withOcc.reduce((best, c) => (c.m.occupancy.peak.total > (best?.total ?? -1)
            ? { ...c.m.occupancy.peak, label: c.label }
            : best), null);
        const peakQueue = withQueue.reduce((best, c) => (c.m.queue.peak && c.m.queue.peak.value > (best?.value ?? -1) ? { ...c.m.queue.peak, color: c.color, label: c.label } : best), null);
        return {
            gameTraffic: avgOf(withTraffic.map((c) => c.m.traffic.game)),
            dayTraffic: avgOf(withTraffic.map((c) => c.m.traffic.day)),
            conversion: withConv.length ? { rate: base > 0 ? Math.round((purchases / base) * 1000) / 10 : 0, purchases, base } : null,
            peakOcc: avgOf(withOcc.map((c) => c.m.occupancy.peak.total)),
            peakPerc: avgOf(withOcc.map((c) => c.m.occupancy.peak.perc)),
            avgOcc: avgOf(withOcc.map((c) => c.m.occupancy.avg)),
            avgOccPerc: avgOf(withOcc.map((c) => c.m.occupancy.avgPerc)),
            queueAvg: avgOf(withQueue.map((c) => c.m.queue.avg)),
            peakQueue,
            peakOccMax,
            counts: { traffic: withTraffic.length, conv: withConv.length, occ: withOcc.length, queue: withQueue.length },
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected, metricsByDate]);

    // Totals across all selected games, and each game's share of traffic
    const totals = useMemo(() => {
        const withTraffic = ready.filter((c) => c.m.traffic);
        if (!withTraffic.length) return null;
        const sum = (pick) => withTraffic.reduce((s, c) => s + (pick(c.m) || 0), 0);
        const ranked = [...withTraffic].sort((a, b) => b.m.traffic.day - a.m.traffic.day);
        return {
            day: sum((m) => m.traffic.day),
            game: sum((m) => m.traffic.game),
            inWindow: sum((m) => m.traffic.inWindow),
            // Only name a most/least game when it's a clear one (no tie)
            most: ranked.length > 1 && ranked[0].m.traffic.day > ranked[1].m.traffic.day ? ranked[0] : null,
            least: ranked.length > 1 && ranked[ranked.length - 1].m.traffic.day < ranked[ranked.length - 2].m.traffic.day ? ranked[ranked.length - 1] : null,
            slices: withTraffic.map((c) => ({
                key: c.date,
                label: `${c.label.short}${c.label.opponent ? ` · ${c.label.opponent}` : ''}`,
                color: c.color,
                value: c.m.traffic.day,
            })),
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected, metricsByDate]);

    // Highest game per row in the summaries (bold), only when comparing 2+ games
    const bestOf = (pick) => {
        if (ready.length < 2) return null;
        const values = ready
            .map((c) => ({ date: c.date, v: pick(c.m) }))
            .filter((x) => typeof x.v === 'number');
        if (values.length < 2) return null;
        const max = Math.max(...values.map((x) => x.v));
        const top = values.filter((x) => x.v === max);
        // Only bold a clear winner, not a tie
        return top.length === 1 ? top[0].date : null;
    };
    const best = {
        game: bestOf((m) => m.traffic?.game),
        day: bestOf((m) => m.traffic?.day),
        win: bestOf((m) => m.traffic?.inWindow),
        conv: bestOf((m) => m.conversion?.rate),
        peak: bestOf((m) => m.occupancy?.peak.total),
        avg: bestOf((m) => m.occupancy?.avg),
        queue: bestOf((m) => m.queue?.avg),
    };

    // Chart: which metric and how games line up
    const metricOptions = [
        ...(hasOccupancy ? [{ value: 'occupancy', label: 'Occupancy' }] : []),
        ...(hasDoors ? [{ value: 'traffic', label: 'Traffic' }] : []),
        ...(hasQueues ? [{ value: 'queue', label: 'Queues' }] : []),
    ];
    const [metric, setMetric] = useState(metricOptions[0]?.value || 'traffic');
    const [align, setAlign] = useState('start');
    // Bucket size for the chart, in minutes (averages for occupancy and queues, totals for traffic)
    const [resolution, setResolution] = useState(15);
    const chart = useMemo(() => {
        const series = ready.map((c) => {
            const within = (pts) => pts.filter((p) => p.m >= c.m.windowStart && p.m <= c.m.windowEnd);
            let points = [];
            if (metric === 'occupancy' && c.m.occupancy) {
                points = bucketSeries(within(occupancyPoints(c.m.occupancy.minutes)), { align, startMinute: c.m.startMinute, size: resolution });
            } else if (metric === 'traffic' && c.m.traffic) {
                points = bucketSeries(within(c.m.traffic.entries), { align, startMinute: c.m.startMinute, size: resolution, agg: 'sum' });
            } else if (metric === 'queue' && c.m.queue) {
                points = bucketSeries(within(c.m.queue.readings), { align, startMinute: c.m.startMinute, size: resolution });
            }
            return { key: c.date, label: `${c.label.short}${c.label.opponent ? ` · ${c.label.opponent}` : ''}`, color: c.color, points };
        });
        const every = resolution === 60 ? 'hour' : resolution === 1 ? 'minute' : `${resolution} min`;
        const avgLabel = resolution === 60 ? 'hourly averages' : resolution === 1 ? 'minute by minute' : `${resolution}-minute averages`;
        const meta = {
            occupancy: { title: 'Occupancy', unit: 'people', yTitle: 'People in store', subtitle: `People in the store, ${avgLabel}.` },
            traffic: { title: 'Traffic', unit: 'entries', yTitle: `Entries per ${every}`, subtitle: `People entering the store, per ${every}. Shows when each rush happened.` },
            queue: { title: 'Queues', unit: 'min', yTitle: 'Queue (min)', subtitle: `Average queue across checkout lines, ${avgLabel}.` },
        }[metric] || { title: 'Through the day', unit: '', yTitle: '', subtitle: '' };
        const startMarkers = ready
            .filter((c) => c.m.startMinute != null)
            .map((c) => ({ minute: c.m.startMinute, color: c.color }));
        return { series, startMarkers, ...meta };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected, metricsByDate, metric, align, resolution]);

    const [zoneMetric, setZoneMetric] = useState('people');

    // Conversion and occupancy: one selected game at a time
    const [convGame, setConvGame] = useState(null);
    const convCards = ready.filter((c) => c.m.conversion && dataByDate[c.date]?.traffic && dataByDate[c.date]?.sales);
    const convDate = convCards.some((c) => c.date === convGame) ? convGame : convCards[0]?.date;
    // Entrance readings (sales sensors excluded) with each reading's sales-sensor counts added, by timestamp
    const convReadings = useMemo(() => {
        const raw = convDate ? dataByDate[convDate] : null;
        if (!raw?.traffic || !raw?.sales) return null;
        const salesIds = currentLocation.doorCounterSensorSalesIds || [];
        const salesAt = new Map(raw.sales.map((row) => [row.timestamp, row]));
        return raw.traffic.map((row) => {
            const sale = salesAt.get(row.timestamp);
            const extra = {};
            salesIds.forEach((id) => { extra[`${id}_out`] = sale?.[`${id}_out`] || 0; });
            return { ...row, ...extra };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [convDate, dataByDate]);

    // Zones: average occupancy per zone in each game's window
    const zoneRows = useMemo(() => {
        const occCards = ready.filter((c) => c.m.occupancy);
        if (!occCards.length || !(currentLocation.zones || []).length) return null;
        const rows = currentLocation.zones.map((z) => {
            const cells = occCards.map((c) => ({
                date: c.date,
                v: c.m.occupancy.zoneAvg[z.id] ?? 0,
                dwell: c.m.occupancy.dwell?.[z.id] || null,
            }));
            const mean = cells.reduce((s, x) => s + x.v, 0) / cells.length;
            const dwells = cells.map((x) => x.dwell?.avg).filter((v) => v != null);
            const dwellMean = dwells.length ? dwells.reduce((a, b) => a + b, 0) / dwells.length : null;
            // Longest single 15-minute stretch across the games
            const peak = cells.reduce((best, x) => (x.dwell?.peak != null && x.dwell.peak > (best?.peak ?? -1)
                ? { peak: x.dwell.peak, minute: x.dwell.peakMinute, date: x.date }
                : best), null);
            return { id: z.id, cells, mean, dwellMean, peak };
        });
        const all = rows.flatMap((r) => r.cells.map((x) => x.v));
        const allDwell = rows.flatMap((r) => r.cells.map((x) => x.dwell?.avg)).filter((v) => v != null);
        return { cards: occCards, rows, all, allDwell };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected, metricsByDate]);

    // Exports wait until every selected game has loaded (no half-loaded CSVs)
    const exportBusy = loading || selected.some((g) => !dataByDate[g.date]);

    // Zones panel exports: the selected games over the time window, as shown in the table
    const exportZones = (metric) => {
        if (!zoneRows || exportBusy) return;
        const isDwell = metric === 'dwell';
        const rows = zoneExportRows(
            zoneRows.cards,
            currentLocation.zones || [],
            zoneName,
            isDwell ? (occ, id) => occ.dwell?.[id]?.avg ?? null : (occ, id) => occ.zoneAvg?.[id] ?? null,
            // Dwell also exports each zone's peak (longest 15-minute stretch) and when it happened
            isDwell ? (occ, id) => {
                const d = occ.dwell?.[id];
                return [d?.peak ?? '', d?.peakMinute != null ? minuteToClock(d.peakMinute) : ''];
            } : undefined
        );
        if (!rows.length) return;
        downloadCsv(
            zoneExportFilename(currentLocation.name, isDwell ? 'dwell' : 'avg-people', zoneRows.cards.map((c) => c.date)),
            buildZoneCsv(rows, isDwell ? 'avgDwellMin' : 'avgPeople', isDwell ? ['peakDwellMin', 'peakTime'] : [])
        );
    };

    const labelFor = (date) => cards.find((c) => c.date === date)?.label;

    // Each selected game's actual clock window for the time window offsets
    const windowChips = ready.length > 0 ? (
        <div className="rx-ov-windows">
            {[...ready].sort((a, b) => (a.date < b.date ? -1 : 1)).map((c) => (
                <span key={c.date} className="rx-ov-window-chip">
                    <span className="cx-ov-swatch" style={{ background: c.color }} />
                    {c.label.short}
                    <strong>{minuteToClock(c.m.windowStart)} – {minuteToClock(c.m.windowEnd)}</strong>
                </span>
            ))}
        </div>
    ) : null;

    // Top and bottom zones for the current metric (a zone is never in both lists)
    const zoneExtremes = (() => {
        if (!zoneRows) return null;
        const isDwell = zoneMetric === 'dwell';
        const valueOf = (r) => (isDwell ? r.dwellMean : r.mean);
        const ranked = zoneRows.rows.filter((r) => valueOf(r) != null).sort((a, b) => valueOf(b) - valueOf(a));
        if (ranked.length < 2) return null;
        const topCount = Math.min(3, Math.ceil(ranked.length / 2));
        const top = ranked.slice(0, topCount);
        const bottom = ranked.slice(topCount).reverse().slice(0, 3);
        const allValues = ranked.map(valueOf);
        // The game where a zone's average people was highest
        const bestGame = (r) => r.cells.reduce((b, x) => (x.v > (b?.v ?? -1) ? x : b), null);
        const card = (r, i, kind) => {
            const v = valueOf(r);
            const heat = isDwell ? dwellHeatColor(v, allValues) : zoneHeatColor(v, allValues, true);
            let sub = null;
            if (isDwell && r.peak) {
                const lab = labelFor(r.peak.date);
                sub = `Peak ~${r.peak.peak} min at ${minuteToClock(r.peak.minute)}${lab ? ` · ${lab.short}` : ''}`;
            } else if (!isDwell) {
                const g = bestGame(r);
                const lab = g ? labelFor(g.date) : null;
                if (g && zoneRows.cards.length > 1) sub = `Highest ${g.v} · ${lab?.short || ''}`;
            }
            return (
                <div key={`${kind}-${r.id}`} className="rx-ov-dwell-card" style={{ '--heat': heat }}>
                    <div className="rx-ov-dwell-rank">#{i + 1}</div>
                    <div className="rx-ov-dwell-name">{zoneName(r.id)}</div>
                    <div className="rx-ov-dwell-val">
                        {isDwell ? `~${Math.round(v)}` : Math.round(v * 10) / 10}
                        <span>{isDwell ? ' min avg' : ' avg people'}</span>
                    </div>
                    {sub && <div className="rx-ov-dwell-peak">{sub}</div>}
                </div>
            );
        };
        return {
            topTitle: isDwell ? 'Longest dwell' : 'Busiest zones',
            bottomTitle: isDwell ? 'Shortest dwell' : 'Quietest zones',
            top: top.map((r, i) => card(r, i, 'top')),
            bottom: bottom.map((r, i) => card(r, i, 'bottom')),
        };
    })();

    const sortedZoneRows = zoneRows
        ? [...zoneRows.rows].sort((a, b) => (zoneMetric === 'dwell' ? (b.dwellMean ?? -1) - (a.dwellMean ?? -1) : b.mean - a.mean))
        : [];

    const gamesWithNoData = ready.filter((c) =>
        !c.m.traffic?.day && !c.m.occupancy?.peak.total && !(c.m.queue?.readings || []).length
    );

    return (
        <div className="rx-ov">
            {loading && (
                <div style={{ position: "fixed", top: 0, left: 75, right: 0, zIndex: 9999 }}>
                    <LinearProgress
                        sx={{
                            backgroundColor: "rgba(2, 166, 147, 0.25)",
                            height: 4,
                            "& .MuiLinearProgress-bar": { backgroundColor: "rgb(2, 166, 147)" },
                        }}
                    />
                </div>
            )}

            {/* Compare bar: chips for the selected games + Add game dropdown */}
            <div className="mb-4 cx-ov-bar-wrap">
                <Panel className="cx-ov-bar">
                    <div className="cx-ov-bar-row">
                        <div className="cx-ov-bar-games">
                            <div className="cx-ov-bar-label">
                                Games
                                <span className="rx-ov-count">{selected.length} of {MAX_GAMES}</span>
                            </div>
                            <div className="cx-ov-chips">
                                {cards.map((c) => (
                                    <span key={c.date} className={`cx-ov-chip cx-fade-in${c.pending ? ' pending' : ''}`}>
                                        <span className="cx-ov-swatch" style={{ background: c.color }} />
                                        <span className="cx-ov-chip-text rx-ov-chip-text">
                                            <span>
                                                {c.label.short}
                                                {c.label.opponent && <span className="cx-ov-chip-sub"> · {c.label.opponent}</span>}
                                            </span>
                                            {/* The game's actual time window for the selected offsets */}
                                            {c.m && (
                                                <span className="rx-ov-chip-window">
                                                    {minuteToClock(c.m.windowStart)} – {minuteToClock(c.m.windowEnd)}
                                                </span>
                                            )}
                                        </span>
                                        {c.pending && <span className="cx-report-spinner cx-ov-chip-spinner" aria-hidden="true" />}
                                        <button
                                            type="button"
                                            className="cx-ov-chip-remove"
                                            aria-label={`Remove ${c.label.short}`}
                                            onClick={() => toggleGame(c.date)}
                                        >
                                            <Icon name="x" />
                                        </button>
                                    </span>
                                ))}
                                <div className="cx-ov-add" ref={pickerRef}>
                                    <button
                                        type="button"
                                        className="cx-ov-add-btn"
                                        onClick={() => setPickerOpen((o) => !o)}
                                        disabled={selected.length >= MAX_GAMES && !pickerOpen}
                                        aria-expanded={pickerOpen}
                                    >
                                        <Icon name="plus-lg" />
                                        {selected.length >= MAX_GAMES ? 'Max 4 games' : hasSelection ? 'Add game' : 'Add games to compare'}
                                    </button>
                                    {pickerOpen && (
                                        <div className="cx-ov-picker" role="dialog" aria-label="Pick games">
                                            <div className="cx-ov-picker-head">
                                                <span>Pick up to 4 games</span>
                                                <button type="button" className="cx-ov-chip-remove" aria-label="Close" onClick={() => setPickerOpen(false)}>
                                                    <Icon name="x-lg" />
                                                </button>
                                            </div>
                                            <SeasonSelector games={games} currentSeason={currentSeason} setCurrentSeason={setCurrentSeason} />
                                            <div className="cx-game-list">
                                                {filteredGames.map((game, gameIndex) => {
                                                    const startDate = game.dates?.[0]?.date;
                                                    const endDate = game.dates?.[game.dates.length - 1]?.date;
                                                    return (
                                                        <div key={`game-${game.teamID}-${gameIndex}`} className="cx-game-row">
                                                            <div className="cx-game-info">
                                                                <img alt="Opponent" width={22} height={22} src={getTeam(currentLocation.league, game.teamID)?.imgURL} />
                                                                <span className="cx-game-name">{getTeam(currentLocation.league, game?.teamID)?.name}</span>
                                                                <span className="cx-game-dates">
                                                                    {moment(startDate).format("MMM D")} - {moment(endDate).format("MMM D")}
                                                                </span>
                                                            </div>
                                                            <div className="cx-game-buttons">
                                                                {game.dates.map((gameDate, dateIndex) => {
                                                                    if (!(new Date() > moment(gameDate.date))) return null;
                                                                    const sel = selected.find((s) => s.date === gameDate.date);
                                                                    return (
                                                                        <button
                                                                            key={gameDate.date}
                                                                            type="button"
                                                                            onClick={() => toggleGame(gameDate.date)}
                                                                            className={`btn cx-game-btn${sel ? ' selected' : ''}`}
                                                                            style={sel ? { backgroundColor: sel.color, borderColor: sel.color, color: '#ffffff' } : undefined}
                                                                        >
                                                                            {dateIndex + 1}
                                                                        </button>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {hasSelection && (
                                    <button type="button" className="cx-ov-clear" onClick={clearGames}>
                                        Clear
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="cx-ov-bar-window">
                            <TimeWindow
                                compact
                                startOffset={timeRange.startOffset}
                                endOffset={timeRange.endOffset}
                                onStartChange={(v) => setTimeRange((t) => ({ ...t, startOffset: v }))}
                                onEndChange={(v) => setTimeRange((t) => ({ ...t, endOffset: v }))}
                                onPreset={(start, end) => setTimeRange({ startOffset: start, endOffset: end })}
                                info={
                                    <InfoTip
                                        title="Time window"
                                        text="Occupancy, zones, queues, entries in window and the chart use this window, measured from each game's start. Day traffic, the phase split and conversion are full-day totals."
                                    />
                                }
                            />
                        </div>
                    </div>
                </Panel>
            </div>

            <Presence
                show={hasSelection}
                className="cx-ov-presence"
                fallback={
                    <div className="cx-empty-state cx-ov-empty-main">
                        <Icon name="bar-chart-line" />
                        <div className="cx-empty-title">No games selected</div>
                        <div>Use <strong>Add games to compare</strong> above to pick up to 4 games and compare traffic, conversion, occupancy and queues side by side.</div>
                    </div>
                }
            >
                <div className="rx-ov-main">
                    {gamesWithNoData.length > 0 && (
                        <div className="cx-notice">
                            <Icon name="info-circle" />
                            No data for {gamesWithNoData.map((c) => c.label.short).join(', ')}.
                        </div>
                    )}

                    {/* Headline numbers */}
                    {ready.length > 0 && (
                        <div className="cx-kpi-grid cx-kpi-grid-auto">
                            {hasDoors && kpi.gameTraffic != null && (
                                <KpiCard
                                    label="Avg game traffic"
                                    info="Store entries from gates open onward, averaged per game. Pregame entries are not included."
                                    value={fmt(kpi.gameTraffic)}
                                >
                                    <div className="cx-kpi-sub">Avg day traffic {fmt(kpi.dayTraffic)}</div>
                                    <div className="cx-kpi-foot">Per game, averaged</div>
                                </KpiCard>
                            )}
                            {hasSales && kpi.conversion && (
                                <KpiCard
                                    label="Overall conversion"
                                    info="Visitors who were associated with a purchase, out of day traffic, with all selected games combined."
                                    value={<>{kpi.conversion.rate}<span className="cx-unit">%</span></>}
                                >
                                    <div className="cx-kpi-sub">{fmt(kpi.conversion.purchases)} of {fmt(kpi.conversion.base)} visitors</div>
                                    <div className="cx-kpi-foot">All selected games</div>
                                </KpiCard>
                            )}
                            {hasOccupancy && kpi.avgOcc != null && (
                                <KpiCard
                                    label="Avg occupancy"
                                    info="People in the store during the time window, averaged per game. The peak is the single most crowded moment across the selected games."
                                    value={<>{fmt(kpi.avgOcc)}<span className="cx-unit">/ {currentLocation.maxOccupancy}</span></>}
                                >
                                    {kpi.peakOccMax && (
                                        <div className="cx-kpi-sub">
                                            Peak {fmt(kpi.peakOccMax.total)}
                                            {kpi.peakOccMax.minute != null && (
                                                <> ({kpi.peakOccMax.label.long}, {minuteToClock(kpi.peakOccMax.minute)})</>
                                            )}
                                        </div>
                                    )}
                                    <div className="cx-kpi-foot">{fmt(kpi.avgOccPerc)}% full on average · per game, averaged</div>
                                </KpiCard>
                            )}
                            {hasQueues && kpi.queueAvg != null && (
                                <KpiCard
                                    label="Avg queue"
                                    info="Average queue across checkout lines during the time window, averaged per game."
                                    value={<>{kpi.queueAvg.toFixed(1)}<span className="cx-unit">min</span></>}
                                >
                                    {kpi.peakQueue && (
                                        <div className="cx-kpi-sub">
                                            Peak {kpi.peakQueue.value} min ({kpi.peakQueue.label.long}, {minuteToClock(kpi.peakQueue.minute)})
                                        </div>
                                    )}
                                    <div className="cx-kpi-foot">Per game, averaged</div>
                                </KpiCard>
                            )}
                        </div>
                    )}

                    {/* Traffic totals across the selected games + share of traffic */}
                    {totals && ready.length > 1 && (
                        <Panel className="rx-ov-panel">
                            <PanelHeader
                                title="Traffic across the games"
                                subtitle={`Store traffic added up for all ${ready.length} selected games, and each game's share of day traffic.`}
                            />
                            <div className="rx-ov-totals">
                                <div className="rx-ov-total-tiles">
                                    <StatTile size="lg" label="Total traffic" value={fmt(totals.day)} caption="Day traffic, all games" />
                                    <StatTile label="Total game traffic" value={fmt(totals.game)} caption="From gates open, all games" />
                                    <StatTile label="Total entries in window" value={fmt(totals.inWindow)} caption="All games" />
                                    {totals.most && (
                                        <StatTile
                                            label="Most traffic"
                                            value={<><span className="cx-ov-swatch" style={{ background: totals.most.color }} /> {totals.most.label.short}</>}
                                            caption={`${fmt(totals.most.m.traffic.day)} people${totals.most.label.opponent ? ` · vs ${totals.most.label.opponent}` : ''}`}
                                        />
                                    )}
                                    {totals.least && (
                                        <StatTile
                                            label="Least traffic"
                                            value={<><span className="cx-ov-swatch" style={{ background: totals.least.color }} /> {totals.least.label.short}</>}
                                            caption={`${fmt(totals.least.m.traffic.day)} people${totals.least.label.opponent ? ` · vs ${totals.least.label.opponent}` : ''}`}
                                        />
                                    )}
                                </div>
                                <TrafficShareDonut slices={totals.slices} total={totals.day} centerLabel="people" />
                            </div>
                        </Panel>
                    )}

                    {/* One card per game */}
                    <Panel className="rx-ov-panel">
                        <PanelHeader
                            title="Game summaries"
                            subtitle={ready.length > 1 ? 'Each selected game. The highest value in each row is bold.' : 'Each selected game.'}
                        />
                        <div className="rx-ov-games">
                            {cards.map((c) => (
                                <div key={c.date} className="rx-ov-game cx-fade-in" style={{ '--game-color': c.color }}>
                                    <div className="rx-ov-game-head">
                                        <span className="cx-ov-swatch" style={{ background: c.color }} />
                                        <div>
                                            <div className="rx-ov-game-date">{c.label.long}</div>
                                            {c.label.sub && <div className="rx-ov-game-sub">{c.label.sub}</div>}
                                        </div>
                                    </div>
                                    {!c.m ? (
                                        <div className="rx-ov-game-loading">
                                            <div className="cx-skel cx-skel-value" />
                                            <div className="cx-skel cx-skel-line" />
                                            <div className="cx-skel cx-skel-line short" />
                                        </div>
                                    ) : (
                                        <>
                                            {c.m.traffic && (
                                                <>
                                                    <div className={`rx-ov-game-big${best.game === c.date ? ' best' : ''}`}>
                                                        {fmt(c.m.traffic.game)}
                                                        <span>game traffic</span>
                                                    </div>
                                                    <PhaseBar phases={c.m.traffic.phases} />
                                                </>
                                            )}
                                            {c.m.conversion && <ConversionBar conversion={c.m.conversion} best={best.conv === c.date} />}
                                            <dl className="rx-ov-rows">
                                                {c.m.traffic && (
                                                    <div className={best.day === c.date ? 'best' : ''}>
                                                        <dt>Day traffic</dt>
                                                        <dd>{fmt(c.m.traffic.day)}</dd>
                                                    </div>
                                                )}
                                                {c.m.traffic && (
                                                    <div className={best.win === c.date ? 'best' : ''}>
                                                        <dt>Entries in window</dt>
                                                        <dd>{fmt(c.m.traffic.inWindow)}</dd>
                                                    </div>
                                                )}
                                                {c.m.occupancy && (
                                                    <div className={best.peak === c.date ? 'best' : ''}>
                                                        <dt>Peak occupancy</dt>
                                                        <dd>
                                                            {fmt(c.m.occupancy.peak.total)}
                                                            {c.m.occupancy.peak.minute != null && <small> at {minuteToClock(c.m.occupancy.peak.minute)}</small>}
                                                        </dd>
                                                    </div>
                                                )}
                                                {c.m.occupancy && (
                                                    <div className={best.avg === c.date ? 'best' : ''}>
                                                        <dt>Avg occupancy</dt>
                                                        <dd>{fmt(c.m.occupancy.avg)} <small>({c.m.occupancy.avgPerc}%)</small></dd>
                                                    </div>
                                                )}
                                                {c.m.occupancy?.busiestZone && (
                                                    <div title="Zone with the most people on average during the time window">
                                                        <dt>Busiest zone</dt>
                                                        <dd className="rx-ov-zone">
                                                            {zoneName(c.m.occupancy.busiestZone.id)}
                                                            <small> · {c.m.occupancy.busiestZone.avg} avg</small>
                                                        </dd>
                                                    </div>
                                                )}
                                                {c.m.occupancy?.longestDwell && (
                                                    <div title="Zone where people stayed longest on average during the time window">
                                                        <dt>Longest dwell</dt>
                                                        <dd className="rx-ov-zone">
                                                            {zoneName(c.m.occupancy.longestDwell.id)}
                                                            <small> · ~{c.m.occupancy.longestDwell.avg} min</small>
                                                        </dd>
                                                    </div>
                                                )}
                                                {c.m.queue && c.m.queue.avg != null && (
                                                    <div className={best.queue === c.date ? 'best' : ''}>
                                                        <dt>Avg queue</dt>
                                                        <dd>
                                                            {c.m.queue.avg} min
                                                            {c.m.queue.peak && <small> · peak {c.m.queue.peak.value}</small>}
                                                        </dd>
                                                    </div>
                                                )}
                                            </dl>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    </Panel>

                    {/* Through the day */}
                    {metricOptions.length > 0 && ready.length > 0 && (
                        <OverviewCompareChart
                            metricOptions={metricOptions}
                            metric={metric}
                            onMetric={setMetric}
                            align={align}
                            onAlign={setAlign}
                            series={chart.series}
                            unit={chart.unit}
                            yTitle={chart.yTitle}
                            subtitle={chart.subtitle}
                            startMarkers={chart.startMarkers}
                            title={chart.title}
                            resolution={resolution}
                            onResolution={setResolution}
                        />
                    )}

                    {/* Conversion and occupancy for one game at a time */}
                    {hasSales && convCards.length > 0 && convReadings && (
                        <Panel className="rx-ov-panel">
                            <PanelHeader
                                title={hasOccupancy ? 'Conversion and occupancy' : 'Conversion'}
                                subtitle={hasOccupancy
                                    ? 'Conversion rate through the game (all purchases ÷ all entries so far), against people in the store.'
                                    : 'Conversion rate through the game (all purchases ÷ all entries so far).'}
                                actions={convCards.length > 1 ? (
                                    <div className="cx-chart-segmented rx-ov-game-switch" role="group" aria-label="Game">
                                        {convCards.map((c) => (
                                            <button
                                                key={c.date}
                                                type="button"
                                                className={`cx-chart-segment ${convDate === c.date ? 'active' : ''}`}
                                                onClick={() => setConvGame(c.date)}
                                            >
                                                <span className="cx-ov-swatch" style={{ background: c.color }} />
                                                {c.label.short}
                                            </button>
                                        ))}
                                    </div>
                                ) : null}
                            />
                            <ConversionOccupancyChart
                                key={convDate}
                                showTitle={false}
                                readings={convReadings}
                                salesIds={currentLocation.doorCounterSensorSalesIds || []}
                                occupancy={hasOccupancy ? dataByDate[convDate]?.occupancy : null}
                                maxOccupancy={currentLocation.maxOccupancy}
                                currentGame={{ date: convDate, ...(findGameByDate(convDate)?.gameDate || {}) }}
                                dayType="game"
                                showPregame={(metricsByDate[convDate]?.traffic?.phases?.pregame || 0) > 0}
                            />
                        </Panel>
                    )}

                    {/* Zones across games: people and dwell */}
                    {zoneRows && (
                        <Panel className="rx-ov-panel">
                            <PanelHeader
                                title="Zones"
                                subtitle={zoneMetric === 'dwell'
                                    ? 'Estimated average time people stayed in each zone during the time window, longest first.'
                                    : 'Average people in each zone during the time window, busiest first. Colors match the store map.'}
                                info="Busiest zone: most people on average. Dwell: estimated time people stay in a zone, from how its occupancy changes against the zones next to it (same estimate as the Charts tab map). Colors rank each value against everything shown, green lowest to red highest."
                                actions={
                                    <div className="rx-ov-zone-actions">
                                        {zoneRows.allDwell.length > 0 && (
                                            <ChartSegmented
                                                options={[{ value: 'people', label: 'Avg people' }, { value: 'dwell', label: 'Dwell time' }]}
                                                value={zoneMetric}
                                                onChange={setZoneMetric}
                                            />
                                        )}
                                        <button
                                            type="button"
                                            className="cx-sort-btn"
                                            onClick={() => exportZones('people')}
                                            disabled={exportBusy}
                                            title={exportBusy ? 'Loading game data…' : 'Average people per zone for the selected games, over the time window'}
                                        >
                                            <Icon name="download" /> Avg people CSV
                                        </button>
                                        {zoneRows.allDwell.length > 0 && (
                                            <button
                                                type="button"
                                                className="cx-sort-btn"
                                                onClick={() => exportZones('dwell')}
                                                disabled={exportBusy}
                                                title={exportBusy ? 'Loading game data…' : 'Average estimated dwell per zone for the selected games, over the time window'}
                                            >
                                                <Icon name="download" /> Dwell CSV
                                            </button>
                                        )}
                                    </div>
                                }
                            />

                            {windowChips && <div className="rx-ov-zone-windows">{windowChips}</div>}

                            {zoneExtremes && (
                                <div className="rx-ov-extremes">
                                    <div className="rx-ov-extreme-group">
                                        <div className="rx-ov-extreme-title"><Icon name="arrow-up" /> {zoneExtremes.topTitle}</div>
                                        <div className="rx-ov-dwell-top">{zoneExtremes.top}</div>
                                    </div>
                                    {zoneExtremes.bottom.length > 0 && (
                                        <div className="rx-ov-extreme-group">
                                            <div className="rx-ov-extreme-title"><Icon name="arrow-down" /> {zoneExtremes.bottomTitle}</div>
                                            <div className="rx-ov-dwell-top">{zoneExtremes.bottom}</div>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="rx-ov-zones-wrap">
                                <table className="rx-ov-zones">
                                    <thead>
                                        <tr>
                                            <th>Zone</th>
                                            {zoneRows.cards.map((c) => (
                                                <th key={c.date}>
                                                    <span className="cx-ov-swatch" style={{ background: c.color }} />
                                                    {c.label.short}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedZoneRows.map((r) => (
                                            <tr key={r.id}>
                                                <th scope="row">
                                                    <span className="rx-ov-zone-name">{zoneName(r.id)}</span>
                                                    <span className="rx-ov-zone-id">Zone {r.id}</span>
                                                </th>
                                                {r.cells.map((cell) => (
                                                    <td key={cell.date}>
                                                        {zoneMetric === 'dwell' ? (
                                                            cell.dwell?.avg != null ? (
                                                                <span className="rx-ov-dwell-cell">
                                                                    <span className="rx-ov-heat" style={{ '--heat': dwellHeatColor(cell.dwell.avg, zoneRows.allDwell) }}>
                                                                        ~{cell.dwell.avg} min
                                                                    </span>
                                                                    {/* Longest 15-minute stretch for this zone in this game */}
                                                                    {cell.dwell.peak != null && (
                                                                        <span className="rx-ov-dwell-cell-peak">
                                                                            peak ~{cell.dwell.peak}{cell.dwell.peakMinute != null ? ` · ${minuteToClock(cell.dwell.peakMinute)}` : ''}
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            ) : <span className="rx-ov-na">--</span>
                                                        ) : (
                                                            <span className="rx-ov-heat" style={{ '--heat': zoneHeatColor(cell.v, zoneRows.all, true) }}>
                                                                {cell.v}
                                                            </span>
                                                        )}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Panel>
                    )}
                </div>
            </Presence>
        </div>
    );
}
